/**
 * <g-multiselect> - Multi-select with badge chips + click-to-open dropdown
 *
 * The "+" button opens the dropdown (the rest of the container is not a click
 * target, and there is no inline typing). A search box appears at the top of
 * the dropdown only when there are more than 5 options to pick from.
 *
 * Declarative usage via native <option> children (consumed on first connect):
 *
 *   <g-multiselect name="tags" placeholder="Pick tags…">
 *     <option value="urgent" selected>Urgent</option>
 *     <option value="bug">Bug</option>
 *     <option value="feature" selected>Feature</option>
 *     <option value="backlog" disabled>Backlog</option>
 *   </g-multiselect>
 *
 * JS API:
 *   .options Array<{value, label, disabled?}>  full option list
 *   .value   Array<string>                     currently-selected values
 *
 * Attributes:
 *   name           used as the name on internal <input type="hidden"> per selected value
 *   placeholder    text shown in the control when nothing is selected
 *   disabled       disables all interaction
 *   remote-search  OPT-IN server-driven search: typing in the search box emits
 *                  a debounced `search` event ({query}) for the host to fetch
 *                  matching options and re-assign `.options`. The search box is
 *                  always shown, selections survive option swaps (labels are
 *                  cached), and local substring filtering still applies to
 *                  whatever options are currently loaded. Absent → classic
 *                  client-side filtering, unchanged.
 *
 * Events:
 *   change   detail: { value, added, removed }   bubbles, composed
 *   search   detail: { query }                   bubbles, composed (remote-search only)
 */

// Host element styling:
//  - display:block + width:100% so the component fills the width its parent
//    gives it (custom elements are inline by default, which would collapse the
//    inner w-full to content width).
//  - Hide raw <option> children until the component consumes them; otherwise
//    they flash as plain text while the HTML parser is still appending children
//    (custom-element upgrade runs before children are parsed).
const STYLE_ID = 'g-multiselect-styles';
if (typeof document !== 'undefined' && !document.getElementById(STYLE_ID)) {
  const s = document.createElement('style');
  s.id = STYLE_ID;
  s.textContent =
    'g-multiselect { display: block; width: 100%; }' +
    // Positioning is owned here (not via Tailwind utility classes) so the
    // dropdown anchors to the control and matches its width in any host app,
    // regardless of which Tailwind classes that app's build happens to ship.
    'g-multiselect [data-gms-root] { position: relative; width: 100%; }' +
    'g-multiselect [data-gms-control] { width: 100%; }' +
    'g-multiselect [data-gms-panel], g-multiselect [data-gms-overflow] { position: absolute; top: calc(100% + 0.25rem); z-index: 50; }' +
    'g-multiselect [data-gms-panel] { left: 0; right: 0; }' +
    'g-multiselect [data-gms-overflow] { right: 0; max-width: 100%; }' +
    'g-multiselect > option { display: none !important; }';
  (document.head || document.documentElement).appendChild(s);
}

// Show the in-dropdown search box only once the selectable pool is larger
// than this. Small lists are faster to scan than to search.
const SEARCH_THRESHOLD = 5;

export class GMultiselect extends HTMLElement {
  constructor() {
    super();
    this._options = [];
    this._selected = [];
    this._filterText = '';
    this._open = false;
    this._overflowOpen = false;
    this._overflowValues = [];
    this._activeIdx = -1;
    this._labelCache = new Map();   // value → label (chips survive remote option swaps)
    this._searchDebounce = null;    // remote-search input debounce timer
    this._renderScheduled = false;
    this._fitScheduled = false;
    this._initialized = false;
  }

  static get observedAttributes() {
    return ['name', 'placeholder', 'disabled'];
  }

  attributeChangedCallback() {
    if (!this.isConnected) return;
    this.scheduleRender();
  }

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;
    this._buildShell();
    this._attachListeners();
    this._parseWhenReady();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this._handleDocClick, true);
    this._childObserver?.disconnect();
    this._ro?.disconnect();
  }

  _parseWhenReady() {
    // Try synchronously — covers the case where the element is upgraded
    // after its children were parsed (define called after parse).
    if (this.querySelector(':scope > option')) {
      this._parseOptionChildren();
      this.scheduleRender();
      return;
    }
    // Otherwise wait for the parser to add option children, then parse
    // on the next frame so all options arrive together.
    this._childObserver = new MutationObserver(() => {
      if (this.querySelector(':scope > option')) {
        this._childObserver.disconnect();
        requestAnimationFrame(() => {
          this._parseOptionChildren();
          this.scheduleRender();
        });
      }
    });
    this._childObserver.observe(this, { childList: true });
    this.scheduleRender();
  }

  // ── Public API ──

  get options() { return this._options; }
  set options(list) {
    this._options = normalizeOptions(list);
    // Remember labels so chips stay readable in remote-search mode, where
    // the loaded option window changes with every query and a selected
    // value's option may not be in the current result set.
    for (const o of this._options) this._labelCache.set(o.value, o.label);
    if (this.remoteSearch) {
      // Remote mode: the option list is a WINDOW into a larger server-side
      // set — a selected value being absent from the current window does
      // not mean it stopped existing. Keep selections.
      this.scheduleRender();
      return;
    }
    // Drop any selected values that no longer exist
    const valid = new Set(this._options.map(o => o.value));
    this._selected = this._selected.filter(v => valid.has(v));
    this.scheduleRender();
  }

  get remoteSearch() { return this.hasAttribute('remote-search'); }

  get value() { return [...this._selected]; }
  set value(list) {
    const next = Array.isArray(list) ? list.map(String) : [];
    const valid = new Set(this._options.map(o => o.value));
    const filtered = dedupe(next.filter(v => valid.has(v)));
    const { added, removed } = diff(this._selected, filtered);
    if (!added.length && !removed.length) return;
    this._selected = filtered;
    this.scheduleRender();
    this._dispatchChange(added, removed);
  }

  // ── Internals ──

  _parseOptionChildren() {
    const opts = [];
    const selected = [];
    Array.from(this.querySelectorAll(':scope > option')).forEach(el => {
      const value = el.hasAttribute('value') ? el.getAttribute('value') : el.textContent.trim();
      const label = el.textContent.trim() || value;
      const disabled = el.hasAttribute('disabled');
      opts.push({ value, label, disabled });
      if (el.hasAttribute('selected')) selected.push(value);
    });
    this._options = normalizeOptions(opts);
    this._selected = dedupe(selected.filter(v => this._options.some(o => o.value === v && !o.disabled)));
    // Consume the option children
    Array.from(this.querySelectorAll(':scope > option')).forEach(el => el.remove());
  }

  _buildShell() {
    this._root = document.createElement('div');
    this._root.className = 'relative w-full';
    this._root.setAttribute('data-gms-root', '');

    this._control = document.createElement('div');
    // Single row, fixed height, clipped — chips never wrap or grow the control.
    // Only the "+" button opens the dropdown, so the shell itself is cursor-default.
    this._control.className = 'input input-bordered h-10 flex items-center gap-1 flex-nowrap overflow-hidden cursor-default';
    this._control.setAttribute('tabindex', '0');
    this._control.setAttribute('role', 'combobox');
    this._control.setAttribute('aria-haspopup', 'listbox');
    this._control.setAttribute('aria-expanded', 'false');
    this._control.setAttribute('data-gms-control', '');

    // Explicit "add" affordance, pinned left — clicking it opens the dropdown.
    this._add = document.createElement('button');
    this._add.type = 'button';
    // btn-square (not btn-circle) so the corner radius follows the theme's
    // field radius — same convention as the surrounding <input> shell.
    this._add.className = 'btn btn-ghost btn-xs btn-square shrink-0';
    this._add.setAttribute('data-gms-add', '');
    this._add.setAttribute('aria-label', 'Add');
    this._add.innerHTML = `<i class="fa-regular fa-plus" aria-hidden="true"></i>`;

    this._chips = document.createElement('span');
    this._chips.className = 'contents';
    this._chips.setAttribute('data-gms-chips', '');

    // Flexible filler: shows the placeholder when nothing is selected and keeps
    // the rest of the control clickable to open the dropdown. Not editable.
    this._placeholder = document.createElement('span');
    this._placeholder.className = 'flex-1 min-w-0 truncate text-base-content/50 select-none';
    this._placeholder.setAttribute('data-gms-placeholder', '');

    this._control.appendChild(this._add);
    this._control.appendChild(this._chips);
    this._control.appendChild(this._placeholder);

    // Dropdown panel — floating container holding an optional search box and
    // the option list.
    this._panel = document.createElement('div');
    // Position/width/z-index come from the injected stylesheet (data-gms-panel);
    // only visual styling lives in classes here.
    this._panel.className = 'bg-base-100 rounded-box shadow-lg border border-base-300 hidden overflow-hidden';
    this._panel.setAttribute('data-gms-panel', '');

    this._searchWrap = document.createElement('div');
    this._searchWrap.className = 'p-2 border-b border-base-300';
    this._search = document.createElement('input');
    this._search.type = 'text';
    this._search.autocomplete = 'off';
    this._search.placeholder = 'Search…';
    this._search.className = 'input input-sm input-bordered w-full';
    this._search.setAttribute('data-gms-search', '');
    this._search.setAttribute('aria-label', 'Search options');
    this._searchWrap.appendChild(this._search);

    this._list = document.createElement('ul');
    this._list.className = 'menu max-h-60 overflow-y-auto p-1 flex-nowrap w-full';
    this._list.setAttribute('role', 'listbox');
    this._list.setAttribute('data-gms-list', '');

    this._panel.appendChild(this._searchWrap);
    this._panel.appendChild(this._list);

    // Overflow popover — lists the hidden selections behind "+N more".
    this._overflow = document.createElement('div');
    // Position/width/z-index come from the injected stylesheet (data-gms-overflow).
    this._overflow.className = 'bg-base-100 rounded-box shadow-lg border border-base-300 ' +
      'max-h-60 overflow-y-auto p-1 min-w-[12rem] hidden';
    this._overflow.setAttribute('role', 'listbox');
    this._overflow.setAttribute('data-gms-overflow', '');

    this._root.appendChild(this._control);
    this._root.appendChild(this._panel);
    this._root.appendChild(this._overflow);

    this._hiddenWrap = document.createElement('span');
    this._hiddenWrap.setAttribute('data-gms-hidden', '');

    this.appendChild(this._root);
    this.appendChild(this._hiddenWrap);
  }

  _attachListeners() {
    this._root.addEventListener('click', this._handleClick);
    this._control.addEventListener('mousedown', this._handleControlMousedown);
    this._control.addEventListener('keydown', this._handleKeydown);
    this._search.addEventListener('input', this._handleSearchInput);
    this._search.addEventListener('keydown', this._handleKeydown);
    document.addEventListener('click', this._handleDocClick, true);

    // Re-fit the chips whenever the control's width changes (window resize,
    // parent layout change, sidebar toggles, etc.).
    if (typeof ResizeObserver !== 'undefined') {
      this._ro = new ResizeObserver(() => this._scheduleFit());
      this._ro.observe(this._control);
    }
  }

  scheduleRender() {
    if (this._renderScheduled) return;
    this._renderScheduled = true;
    requestAnimationFrame(() => {
      this._renderScheduled = false;
      this._render();
    });
  }

  _render() {
    if (!this._root) return;
    const disabled = this.hasAttribute('disabled');
    this._root.classList.toggle('pointer-events-none', disabled);
    this._root.classList.toggle('opacity-60', disabled);
    this._add.disabled = disabled;
    this._search.disabled = disabled;
    this._placeholder.textContent = this._selected.length ? '' : (this.getAttribute('placeholder') || '');

    // Chips. Every selected value is rendered; _fit() (called at the end of
    // this method, and on resize) then measures the row and folds whatever
    // doesn't fit into the "+N more" chip. Long labels truncate with ellipsis.
    const chipHtml = (v) => `
      <span class="badge badge-primary gap-1 min-w-0 max-w-[12rem] shrink-0 rounded-[var(--radius-field)]" data-gms-chip data-value="${escapeHtml(v)}">
        <span class="truncate min-w-0">${escapeHtml(this._labelOf(v))}</span>
        <button type="button" class="cursor-pointer opacity-70 hover:opacity-100 leading-none shrink-0"
                aria-label="Remove ${escapeHtml(this._labelOf(v))}" data-gms-remove="${escapeHtml(v)}"
                tabindex="-1">&times;</button>
      </span>`;

    const chevron = `<i class="fa-regular fa-chevron-down text-[0.65rem] transition-transform ${this._overflowOpen ? 'rotate-180' : ''}" aria-hidden="true"></i>`;
    // "+N more" chip — present in the DOM but hidden until _fit() needs it.
    const moreHtml = `
      <button type="button" data-gms-more aria-haspopup="listbox" aria-expanded="${this._overflowOpen}"
              class="badge badge-ghost gap-1 shrink-0 cursor-pointer whitespace-nowrap rounded-[var(--radius-field)] hidden">
        <span data-gms-more-count>+0 more</span>${chevron}
      </button>`;

    this._chips.innerHTML = this._selected.map(chipHtml).join('') + moreHtml;

    // Dropdown list. The search box only appears once the pool of selectable
    // options (not disabled, not already chosen) exceeds SEARCH_THRESHOLD.
    const poolCount = this._options.filter(o => !o.disabled && !this._selected.includes(o.value)).length;
    // Remote-search: the loaded options are a capped window into a larger
    // server-side set — searching is the point, so the box always shows.
    const showSearch = poolCount > SEARCH_THRESHOLD || this.remoteSearch;
    if (!showSearch) this._filterText = '';
    this._searchWrap.classList.toggle('hidden', !showSearch);

    const visible = this._visibleOptions();
    if (this._activeIdx >= visible.length) this._activeIdx = visible.length - 1;
    if (this._activeIdx < 0 && visible.length) this._activeIdx = 0;

    if (visible.length === 0) {
      this._list.innerHTML = `<li class="px-3 py-2 text-base-content/60 text-sm pointer-events-none">No matches</li>`;
    } else {
      this._list.innerHTML = visible.map((o, i) => {
        const active = i === this._activeIdx ? 'bg-base-200' : '';
        return `<li>
          <a data-gms-option data-value="${escapeHtml(o.value)}" role="option"
             aria-selected="false" tabindex="-1" class="${active}">${escapeHtml(o.label)}</a>
        </li>`;
      }).join('');
    }

    this._panel.classList.toggle('hidden', !this._open);
    this._control.setAttribute('aria-expanded', String(this._open));

    // Scroll active into view
    if (this._open && this._activeIdx >= 0) {
      const opts = this._list.querySelectorAll('[data-gms-option]');
      opts[this._activeIdx]?.scrollIntoView({ block: 'nearest' });
    }

    // Hidden inputs
    const name = this.getAttribute('name');
    if (name) {
      this._hiddenWrap.innerHTML = this._selected.map(v =>
        `<input type="hidden" name="${escapeHtml(name)}" value="${escapeHtml(v)}">`
      ).join('');
    } else {
      this._hiddenWrap.innerHTML = '';
    }

    // Measure the freshly-rendered chips and fold the overflow into "+N more".
    this._fit();
  }

  _labelOf(v) {
    const o = this._options.find(o => o.value === v);
    if (o) return o.label;
    // Remote-search: the option may have scrolled out of the loaded window —
    // the cache keeps the chip label readable instead of showing a raw id.
    const cached = this._labelCache.get(v);
    return cached != null ? cached : v;
  }

  _scheduleFit() {
    if (this._fitScheduled) return;
    this._fitScheduled = true;
    requestAnimationFrame(() => {
      this._fitScheduled = false;
      this._fit();
    });
  }

  // Single-row overflow: hide the oldest chips (from the left) until the latest
  // chip and the "+N more" chip both fit. The latest selection is never hidden,
  // so adding a value always keeps it visible.
  _fit() {
    if (!this._chips || !this._control) return;
    const chips = Array.from(this._chips.querySelectorAll('[data-gms-chip]'));
    const more = this._chips.querySelector('[data-gms-more]');
    const count = more && more.querySelector('[data-gms-more-count]');

    // Reset to "all chips visible, no more chip".
    chips.forEach(c => c.classList.remove('hidden'));
    more?.classList.add('hidden');
    this._overflowValues = [];

    // Can't measure when not laid out (detached / display:none ancestor).
    if (!chips.length || !this._control.clientWidth) {
      this._renderOverflowPopover();
      return;
    }

    const cs = getComputedStyle(this._control);
    const rightLimit = this._control.getBoundingClientRect().right - (parseFloat(cs.paddingRight) || 0);

    // Fast path: everything already fits.
    if (chips[chips.length - 1].getBoundingClientRect().right <= rightLimit) {
      this._renderOverflowPopover();
      return;
    }

    // Overflow needed — reveal the more chip and hide the oldest chips until it
    // (and therefore the visible chips before it) fit within the row.
    more?.classList.remove('hidden');
    const hidden = [];
    for (let i = 0; i < chips.length - 1; i++) {
      if (more && more.getBoundingClientRect().right <= rightLimit) break;
      chips[i].classList.add('hidden');
      hidden.push(chips[i].getAttribute('data-value'));
      if (count) count.textContent = `+${hidden.length} more`;
    }
    this._overflowValues = hidden;
    if (count) count.textContent = `+${hidden.length} more`;
    this._renderOverflowPopover();
  }

  _renderOverflowPopover() {
    const vals = this._overflowValues || [];
    if (!vals.length) this._overflowOpen = false;
    this._overflow.innerHTML = vals.map(v => `
      <div class="flex items-center justify-between gap-2 px-2 py-1 rounded hover:bg-base-200" data-value="${escapeHtml(v)}">
        <span class="truncate min-w-0 text-sm text-base-content">${escapeHtml(this._labelOf(v))}</span>
        <button type="button" class="cursor-pointer opacity-70 hover:opacity-100 leading-none shrink-0 text-base-content"
                aria-label="Remove ${escapeHtml(this._labelOf(v))}" data-gms-remove="${escapeHtml(v)}"
                tabindex="-1">&times;</button>
      </div>`).join('');
    this._overflow.classList.toggle('hidden', !(this._overflowOpen && vals.length));
  }

  _visibleOptions() {
    const filter = this._filterText.trim().toLowerCase();
    return this._options.filter(o =>
      !o.disabled &&
      !this._selected.includes(o.value) &&
      (filter === '' || o.label.toLowerCase().includes(filter))
    );
  }

  _addValue(v) {
    if (!v) return;
    const opt = this._options.find(o => o.value === v);
    if (!opt || opt.disabled) return;
    if (this._selected.includes(v)) return;
    this._selected = [...this._selected, v];
    this._activeIdx = 0;
    this._resetSearch();
    this._open = false;
    this.scheduleRender();
    this._dispatchChange([v], []);
  }

  _resetSearch() {
    this._filterText = '';
    if (this._search) this._search.value = '';
  }

  _removeValue(v) {
    if (!this._selected.includes(v)) return;
    this._selected = this._selected.filter(x => x !== v);
    // _fit() recomputes the overflow set and closes the popover if it empties.
    this.scheduleRender();
    this._dispatchChange([], [v]);
  }

  _open_() {
    if (this._open || this.hasAttribute('disabled')) return;
    this._open = true;
    this._overflowOpen = false;
    this._activeIdx = this._visibleOptions().length ? 0 : -1;
    this.scheduleRender();
    // After the panel renders, move focus to the search box when it is shown,
    // otherwise keep focus on the control for keyboard navigation.
    requestAnimationFrame(() => {
      if (!this._open) return;
      if (!this._searchWrap.classList.contains('hidden')) this._search.focus();
      else this._control.focus();
    });
  }

  _close() {
    if (!this._open) return;
    this._open = false;
    this._activeIdx = -1;
    this._resetSearch();
    this.scheduleRender();
  }

  _toggleOverflow() {
    if (this.hasAttribute('disabled')) return;
    this._overflowOpen = !this._overflowOpen;
    if (this._overflowOpen) this._open = false;
    this.scheduleRender();
  }

  _closeOverflow() {
    if (!this._overflowOpen) return;
    this._overflowOpen = false;
    this.scheduleRender();
  }

  _dispatchChange(added, removed) {
    this.dispatchEvent(new CustomEvent('change', {
      detail: { value: [...this._selected], added, removed },
      bubbles: true,
      composed: true,
    }));
  }

  // ── Event handlers ──

  _handleClick = (e) => {
    if (this.hasAttribute('disabled')) return;
    const more = e.target.closest('[data-gms-more]');
    if (more && this._root.contains(more)) {
      e.preventDefault();
      this._toggleOverflow();
      return;
    }
    const remove = e.target.closest('[data-gms-remove]');
    if (remove && this._root.contains(remove)) {
      e.preventDefault();
      this._removeValue(remove.getAttribute('data-gms-remove'));
      // Removals keep the overflow popover open so several can be cleared.
      return;
    }
    const opt = e.target.closest('[data-gms-option]');
    if (opt && this._list.contains(opt)) {
      e.preventDefault();
      this._addValue(opt.getAttribute('data-value'));
      return;
    }
  };

  _handleControlMousedown = (e) => {
    if (this.hasAttribute('disabled')) return;
    // Only the "+" button toggles the dropdown — clicking the rest of the
    // container (chips, empty space) does nothing.
    if (!e.target.closest('[data-gms-add]')) return;
    e.preventDefault();
    if (this._open) this._close();
    else this._open_();
  };

  _handleSearchInput = () => {
    this._filterText = this._search.value;
    this._activeIdx = 0;
    this.scheduleRender();
    // Remote-search: hand the query to the host (debounced) so it can fetch
    // a matching option window and re-assign `.options`. Local substring
    // filtering above still narrows whatever is currently loaded meanwhile.
    if (this.remoteSearch) {
      clearTimeout(this._searchDebounce);
      const query = this._filterText.trim();
      this._searchDebounce = setTimeout(() => {
        this.dispatchEvent(new CustomEvent('search', {
          detail: { query }, bubbles: true, composed: true,
        }));
      }, 300);
    }
  };

  _handleKeydown = (e) => {
    if (this.hasAttribute('disabled')) return;
    if (!this._open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        e.preventDefault();
        this._open_();
      }
      return;
    }
    const visible = this._visibleOptions();
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!visible.length) return;
      this._activeIdx = (this._activeIdx + 1) % visible.length;
      this.scheduleRender();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!visible.length) return;
      this._activeIdx = (this._activeIdx - 1 + visible.length) % visible.length;
      this.scheduleRender();
    } else if (e.key === 'Enter') {
      if (this._activeIdx >= 0 && visible[this._activeIdx]) {
        e.preventDefault();
        this._addValue(visible[this._activeIdx].value);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      this._close();
    }
  };

  _handleDocClick = (e) => {
    if (this.contains(e.target)) return;
    if (this._open) this._close();
    if (this._overflowOpen) this._closeOverflow();
  };
}

function normalizeOptions(list) {
  if (!Array.isArray(list)) return [];
  const seen = new Set();
  const out = [];
  for (const item of list) {
    if (!item) continue;
    const value = String(item.value ?? '').trim();
    if (!value || seen.has(value)) continue;
    seen.add(value);
    out.push({
      value,
      label: String(item.label ?? value),
      disabled: !!item.disabled,
    });
  }
  return out;
}

function dedupe(arr) {
  const seen = new Set();
  const out = [];
  for (const v of arr) {
    if (seen.has(v)) continue;
    seen.add(v);
    out.push(v);
  }
  return out;
}

function diff(prev, next) {
  const prevSet = new Set(prev);
  const nextSet = new Set(next);
  const added = next.filter(v => !prevSet.has(v));
  const removed = prev.filter(v => !nextSet.has(v));
  return { added, removed };
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
}

if (!customElements.get('g-multiselect')) {
  customElements.define('g-multiselect', GMultiselect);
}
